import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import FallbackImage from '../common/FallbackImage';
import { usePostHog } from 'posthog-js/react';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useResponsive } from '../../hooks/useResponsive';
import { useTier } from '../../context/TierContext';
import {
  HIDDEN_SIGNALS,
  SIGNAL_FRIENDLY_LABELS,
  parsePriceVsMarket,
} from '../../config/listingsConfig';
import { STATUS_LABELS } from '../../config/negotiationConfig';
import { getNegotiationCapCopy } from '../../utils/negotiation/capCopy';
import OverallMatchBadge from './OverallMatchBadge';
import ListingBadgeStack from './ListingBadgeStack';
import ProLock from '../common/ProLock';
import { buildListingBadges } from '../../utils/listingBadges';

const DESKTOP_CARD_WIDTH = 940;
const DESKTOP_IMAGE_WIDTH = 453;

/**
 * Format price in GBP with thousands separator.
 */
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '--';
  return `£${Number(price).toLocaleString('en-GB')}`;
};

/**
 * Format mileage with comma separator and "mi" suffix.
 */
const formatMileage = (miles) => {
  if (miles == null || isNaN(miles)) return '--';
  return `${Number(miles).toLocaleString('en-GB')} mi`;
};

/**
 * FeedListingCard - Listing card for the unified feed.
 *
 * Mobile: vertical layout (image top, details below).
 * Desktop: horizontal layout (image left ~40%, details right) within 772px column.
 * Sold: grayscale image, "SOLD" badge, 50% opacity content, non-clickable.
 *
 * @param {Object} props
 * @param {Object} props.listing - Feed entry from user_listing_feeds
 * @param {Function} props.onPress - Card tap handler
 * @param {Function} props.onSave - Save toggle handler (receives feed_entry_id)
 * @param {boolean} [props.isSaving] - Whether save is in-flight
 * @param {number} [props.index] - Card index in list
 * @param {Function} [props.onNegotiate] - Negotiate CTA handler
 * @param {boolean} [props.isNegotiateDisabled] - Whether negotiate button is disabled
 * @param {number} [props.negotiateLimit] - Active-negotiation cap for tooltip copy
 * @param {string} [props.negotiationStatus] - Existing negotiation status for this listing
 * @param {boolean} [props.isBlocklisted] - Whether the dealer is blocklisted
 */
const FeedListingCard = memo(({ listing, onPress, onSave, onDismiss, isSaving = false, index, isTopMatch = false, isBestDeal = false, onNegotiate, isNegotiateDisabled = false, negotiateLimit = 5, negotiationStatus, isBlocklisted = false, isInComparisonMode = false, isLocked = false, onCompareSelect = null, isSelectedForCompare = false }) => {
  const { isMobile } = useResponsive();
  const { hasProAccess, activeModelId, triggerUpgradeModal, trialUnlockEligible } = useTier();
  const posthog = usePostHog();


  const snapshot = listing?.listing_snapshot || {};
  const {
    make,
    model,
    trim: variant,
    year,
    price,
    miles,
    transmission,
    distance_miles: distanceMiles,
    city,
    photo_url: photoUrl,
  } = snapshot;

  const score = listing?.score ?? 0;
  const isSaved = !!listing?.saved_at;
  const isSold = listing?.status !== 'active';
  const isExternalUrl = listing?.source === 'external_url';
  const isPrivateSeller = snapshot?.is_private_seller === true;

  // All photo links for carousel
  const photoLinks = useMemo(() => {
    const links = snapshot?.photo_links;
    if (Array.isArray(links) && links.length > 0) return links;
    return photoUrl ? [photoUrl] : [];
  }, [snapshot?.photo_links, photoUrl]);

  // Desktop hover carousel state
  const [isHovered, setIsHovered] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [showNegLimitInfo, setShowNegLimitInfo] = useState(false);
  const negLimitTimerRef = useRef(null);
  useEffect(() => () => {
    if (negLimitTimerRef.current) clearTimeout(negLimitTimerRef.current);
  }, []);
  const hasMultiplePhotos = photoLinks.length > 1;

  const currentImageUri = photoLinks[photoIndex] || null;
  // Model AI image, attached by get-unified-listings-feed; used as the display-time fallback
  // when the dealer photo fails to load (see broken-photo-model-fallback spec).
  const modelImageUrl = listing?.modelImageUrl || null;
  const hasAnyImage = !!currentImageUri || !!modelImageUrl;

  // Build a personalized "why this matches" snippet from top scoring signals + snapshot data
  const matchSnippet = useMemo(() => {
    const breakdown = listing?.score_breakdown;
    if (!Array.isArray(breakdown)) return null;

    const dom = snapshot?.dom;
    // Price drop tracked on the feed entry itself
    const priceChangeAmount = listing?.price_change_amount;

    // Sort signals by weighted value (highest impact first), filter to strong signals
    const strong = [...breakdown]
      .filter((s) => s?.value >= 60 && !HIDDEN_SIGNALS.has(s.signal))
      .sort((a, b) => (b.weightedValue || b.value) - (a.weightedValue || a.value));

    const parts = [];

    for (const s of strong) {
      if (parts.length >= 3) break;

      switch (s.signal) {
        case 'price_vs_market':
          // The below-market £ delta is promoted to its own money pill (see
          // moneyDelta below), so it is intentionally NOT added to the generic
          // snippet line, it must not compete as one of several equal phrases.
          break;
        case 'mileage':
          if (s.value >= 70) parts.push('low mileage for year');
          break;
        case 'distance':
          if (distanceMiles != null && distanceMiles <= 30) {
            parts.push('close to you');
          }
          break;
        case 'dom':
          if (dom != null && dom <= 7) parts.push('just listed');
          else if (dom != null && dom > 60) parts.push('on market a while, room to negotiate');
          break;
        case 'price_trend':
          if (priceChangeAmount != null && priceChangeAmount < 0) {
            parts.push(`price dropped £${Math.abs(Math.round(priceChangeAmount)).toLocaleString('en-GB')}`);
          }
          break;
        case 'owners':
          const count = Number(snapshot?.num_owners);
          if (count === 1) parts.push('1 previous owner');
          else if (count > 0 && count <= 3) parts.push(`${count} previous owners`);
          break;
        case 'ulez':
          if (s.value >= 80) parts.push('ULEZ compliant');
          break;
        case 'fuel_match':
          if (s.value >= 80) parts.push('fuel type match');
          break;
        default:
          break;
      }
    }

    if (parts.length === 0) return null;
    // Capitalize first part, join with commas
    const sentence = parts[0].charAt(0).toUpperCase() + parts[0].slice(1) +
      (parts.length > 1 ? ', ' + parts.slice(1).join(', ') : '');
    return sentence;
  }, [listing?.score_breakdown, listing?.price_change_amount, snapshot?.dom, snapshot?.num_owners, distanceMiles]);

  // Build a single caution line from the weakest signal
  const cautionSnippet = useMemo(() => {
    const breakdown = listing?.score_breakdown;
    if (!Array.isArray(breakdown)) return null;

    // Find the weakest signal (lowest value, excluding hidden)
    const weak = [...breakdown]
      .filter((s) => s?.value != null && !HIDDEN_SIGNALS.has(s.signal) && s.value < 50)
      .sort((a, b) => a.value - b.value);

    if (weak.length === 0) return null;

    const s = weak[0];
    switch (s.signal) {
      case 'price_vs_market': {
        const explanation = s.explanation || '';
        const aboveMatch = explanation.match(/£([\d,]+)\s+above/i);
        if (aboveMatch) {
          const amount = parseInt(aboveMatch[1].replace(/,/g, ''), 10);
          if (amount >= 200) return `£${amount.toLocaleString('en-GB')} above market average`;
        }
        return 'priced above market average';
      }
      case 'mileage':
        return 'higher mileage than average';
      case 'distance':
        if (distanceMiles != null && distanceMiles > 50) return `${Math.round(distanceMiles)} miles away`;
        return 'further away from you';
      case 'owners': {
        const count = Number(snapshot?.num_owners);
        if (count >= 4) return `${count} previous owners`;
        return 'multiple previous owners';
      }
      case 'ulez':
        return 'not ULEZ compliant';
      case 'fuel_match':
        return "doesn't match your fuel preference";
      case 'transmission_match':
        return "doesn't match your preferred transmission";
      case 'year_match':
        return 'older than your preferred year range';
      default:
        return null;
    }
  }, [listing?.score_breakdown, snapshot?.num_owners, distanceMiles]);

  // Below-market money delta, promoted from the buried snippet into a distinct
  // pill on the card. Above-market keeps its caution-snippet treatment.
  const moneyDelta = useMemo(() => {
    const delta = parsePriceVsMarket(listing?.score_breakdown);
    return delta && delta.direction === 'below' ? delta : null;
  }, [listing?.score_breakdown]);

  const handlePress = useCallback(() => {
    if (!isSold && onPress) onPress(listing);
  }, [isSold, onPress, listing]);

  const handleSave = useCallback(
    (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (onSave) onSave(listing?.id);
    },
    [onSave, listing?.id]
  );

  const handleDismiss = useCallback(
    (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (onDismiss) onDismiss(listing?.feed_entry_id);
    },
    [onDismiss, listing?.feed_entry_id]
  );

  const handlePhotoPrev = useCallback((e) => {
    e?.stopPropagation?.();
    setPhotoIndex((prev) => (prev > 0 ? prev - 1 : photoLinks.length - 1));
  }, [photoLinks.length]);

  const handlePhotoNext = useCallback((e) => {
    e?.stopPropagation?.();
    setPhotoIndex((prev) => (prev < photoLinks.length - 1 ? prev + 1 : 0));
  }, [photoLinks.length]);

  const handleNegotiate = useCallback(
    (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (isNegotiateDisabled) {
        setShowNegLimitInfo(true);
        if (negLimitTimerRef.current) clearTimeout(negLimitTimerRef.current);
        negLimitTimerRef.current = setTimeout(() => setShowNegLimitInfo(false), 4500);
        return;
      }
      if (onNegotiate) onNegotiate(listing);
    },
    [onNegotiate, listing, isNegotiateDisabled]
  );

  if (!listing) return null;

  const title = [year, make, model, variant].filter(Boolean).join(' ');

  // --- Shared sub-components ---

  // Drop UI in the details area only surfaces while the event is fresh
  // (≤6 days); the same gate is applied inside buildListingBadges for the
  // PRICE DROP image badge. Kept locally for priceDropAmount/previousPrice.
  const isRecentDrop = listing?.price_changed_at
    && (Date.now() - new Date(listing.price_changed_at).getTime()) < 6 * 86400000;
  const hasPriceDrop = isRecentDrop
    && listing?.price_change_direction === 'down'
    && listing?.price_change_amount < 0;

  const badges = buildListingBadges(listing, { isTopMatch, isBestDeal });
  const badgeOverlay = <ListingBadgeStack badges={badges} />;

  const actionButtons = (
    <View style={styles.actionButtonsRow}>
      {false && onDismiss && (
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleDismiss}
          activeOpacity={0.7}
          accessibilityLabel="Dismiss listing"
        >
          <MaterialCommunityIcons
            name="close"
            size={20}
            color="#FFFFFF"
          />
        </TouchableOpacity>
      )}
      <ProLock mode="badge" feature="saved_listings">
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleSave}
          disabled={isSaving}
          activeOpacity={0.7}
          accessibilityLabel={isSaved ? 'Watching' : 'Watch price'}
        >
          <MaterialCommunityIcons
            name={isSaved ? 'heart' : 'heart-outline'}
            size={20}
            color="#FFFFFF"
          />
        </TouchableOpacity>
      </ProLock>
    </View>
  );

  const showCarouselControls = !isMobile && isHovered && hasMultiplePhotos;
  const dotCount = Math.min(photoLinks.length, 7);

  // Declared above imageComponent so it can be referenced inside the image
  // container (anchors the overlay to the image bounds, not the whole card).
  const handleUnlockTap = useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    posthog?.capture('locked_listing_unlock_tapped', {
      feed_entry_id: listing?.id,
      card_index: index,
      below_market_delta: moneyDelta?.amount ?? null,
    });
    triggerUpgradeModal?.({ source: 'cap_feed_listings', belowMarketDelta: moneyDelta?.amount ?? null });
  }, [posthog, listing?.id, index, triggerUpgradeModal, moneyDelta]);

  const cardUnlockOverlay = isLocked ? (
    <Pressable
      style={styles.cardUnlockOverlay}
      onPress={handleUnlockTap}
      accessibilityRole="button"
      accessibilityLabel="Unlock with Pro"
    >
      <View style={styles.unlockButton}>
        <MaterialCommunityIcons name="lock-outline" size={14} color="#FFFFFF" />
        <Text style={styles.unlockButtonText}>{trialUnlockEligible ? 'Unlock' : 'Unlock with Pro'}</Text>
      </View>
    </Pressable>
  ) : null;

  const imageComponent = (
    <View
      style={isMobile ? styles.imageMobile : styles.imageDesktop}
      onMouseEnter={!isMobile ? () => setIsHovered(true) : undefined}
      onMouseLeave={!isMobile ? () => { setIsHovered(false); setPhotoIndex(0); } : undefined}
    >
      {hasAnyImage ? (
        <FallbackImage
          primitive="expo"
          dealerPhotoUrl={currentImageUri}
          modelImageUrl={modelImageUrl}
          style={[
            StyleSheet.absoluteFill,
            isLocked ? styles.imageLocked : isSold && styles.imageGrayscale,
          ]}
          contentFit="cover"
          cachePolicy="disk"
          transition={200}
          placeholder={require('../../../assets/images/car-placeholder.png')}
        />
      ) : (
        <View style={[
          StyleSheet.absoluteFill,
          styles.placeholderBg,
          isLocked ? styles.imageLocked : isSold && styles.imageGrayscale,
        ]}>
          <MaterialCommunityIcons name="car-side" size={48} color="rgba(255,255,255,0.15)" />
        </View>
      )}
      {badgeOverlay}
      {actionButtons}
      {onCompareSelect && !isLocked && (
        <TouchableOpacity
          style={[styles.compareCheckbox, isSelectedForCompare && styles.compareCheckboxSelected]}
          onPress={(e) => { e.stopPropagation?.(); onCompareSelect(listing); }}
          activeOpacity={0.8}
          accessibilityLabel={isSelectedForCompare ? 'Remove from compare' : 'Add to compare'}
        >
          {isSelectedForCompare && (
            <MaterialCommunityIcons name="check" size={14} color="#FFFFFF" />
          )}
        </TouchableOpacity>
      )}
      {showCarouselControls && (
        <>
          <Pressable style={styles.carouselArrowLeft} onPress={handlePhotoPrev}>
            <MaterialCommunityIcons name="chevron-left" size={20} color="#FFFFFF" />
          </Pressable>
          <Pressable style={styles.carouselArrowRight} onPress={handlePhotoNext}>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#FFFFFF" />
          </Pressable>
          <View style={styles.carouselDots}>
            {Array.from({ length: dotCount }).map((_, i) => (
              <View
                key={i}
                style={[styles.carouselDot, i === photoIndex && styles.carouselDotActive]}
              />
            ))}
          </View>
        </>
      )}
      {/* Mobile only — anchors the overlay to the image div so it centers on
          the image. Desktop renders the overlay at card level (sibling) so
          it centers across the full horizontal card layout. */}
      {isMobile && cardUnlockOverlay}
    </View>
  );

  const specsRow = (
    <View style={[styles.specsRow, !isMobile && styles.specsRowDesktop]}>
      {transmission && (
        <View style={styles.specItem}>
          <MaterialCommunityIcons name="car-shift-pattern" size={20} color={REDESIGN_COLORS.textSecondary} />
          <Text style={styles.specText}>{transmission}</Text>
        </View>
      )}
      {miles != null && (
        <View style={styles.specItem}>
          <MaterialCommunityIcons name="speedometer" size={20} color={REDESIGN_COLORS.textSecondary} />
          <Text style={styles.specText}>{formatMileage(miles)}</Text>
        </View>
      )}
      {(city || distanceMiles != null) && (
        <View style={styles.specItem}>
          <MaterialCommunityIcons name="map-marker-distance" size={20} color={REDESIGN_COLORS.textSecondary} />
          <Text style={[styles.specText, styles.specTextDistance]}>
            {city && distanceMiles != null
              ? `${city} · ${Math.round(distanceMiles)} mi`
              : city || `${Math.round(distanceMiles)} mi`}
          </Text>
        </View>
      )}
      {isPrivateSeller && (
        <View style={styles.specItem}>
          <MaterialCommunityIcons name="account" size={20} color="#FF9F43" />
          <Text style={[styles.specText, { color: '#FF9F43' }]}>Private seller</Text>
        </View>
      )}
    </View>
  );

  // When the money pill leads, it absorbs the large top push that normally
  // spaces the snippets away from the specs row; the snippet then follows it
  // tightly so the two don't double-gap.
  const snippetRow = matchSnippet ? (
    <View style={[
      styles.snippetRow,
      isMobile && styles.snippetRowMobile,
      moneyDelta && styles.snippetRowTight,
    ]}>
      <MaterialCommunityIcons name="check-circle" size={20} color="#3AE869" />
      <Text style={styles.snippetText}>{matchSnippet}</Text>
    </View>
  ) : null;

  const cautionRow = cautionSnippet ? (
    <View style={styles.cautionRow}>
      <MaterialCommunityIcons name="alert-circle" size={20} color="#FF9F43" />
      <Text style={styles.cautionText}>{cautionSnippet}</Text>
    </View>
  ) : null;

  const moneyPill = moneyDelta ? (
    <View style={[styles.moneyPill, isMobile && styles.moneyPillMobile]}>
      <MaterialCommunityIcons name="tag-outline" size={16} color="#3AE869" />
      <Text style={styles.moneyPillText}>{moneyDelta.label}</Text>
    </View>
  ) : null;

  const snippetsBlock = (moneyPill || snippetRow || cautionRow) ? (
    <View style={isMobile ? styles.snippetsBlockMobile : null}>
      {moneyPill}
      {snippetRow}
      {cautionRow}
    </View>
  ) : null;

  const isActiveModelListing = !hasProAccess && listing?.model_id === activeModelId;
  const shouldBlurMatchBadge = !hasProAccess && (
    !isActiveModelListing || isInComparisonMode
  );

  const handleBlurredBadgeTap = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (posthog) {
      posthog.capture('match_badge_blurred_unlock_tapped', {
        feed_entry_id: listing?.id,
        is_external_url: listing?.source === 'external_url',
        is_comparison_mode: isInComparisonMode,
      });
    }
    triggerUpgradeModal?.({ source: 'blurred_match_badge' });
  };

  const scoreBadgeElement = (
    <ProLock mode={shouldBlurMatchBadge ? 'blur' : null} feature="match_badge">
      <OverallMatchBadge listing={listing} isExternalUrl={isExternalUrl} />
    </ProLock>
  );

  const priceDropAmount = hasPriceDrop ? Math.abs(listing.price_change_amount) : null;
  const previousPrice = hasPriceDrop ? (listing?.previous_price ?? null) : null;
  const displayPrice = listing?.current_price ?? price;

  const bottomRow = (
    <View style={[styles.bottomRow, !isMobile && styles.bottomRowDesktop]}>
      {shouldBlurMatchBadge ? (
        <Pressable onPress={handleBlurredBadgeTap} accessibilityRole="button" accessibilityLabel="Unlock match score with Pro">
          {scoreBadgeElement}
        </Pressable>
      ) : (
        scoreBadgeElement
      )}
      <View style={styles.priceBlock}>
        {priceDropAmount != null && (
          <View style={styles.priceDropRow}>
            <View style={styles.priceDropChip}>
              <MaterialCommunityIcons name="arrow-down" size={12} color="#16A34A" />
              <Text style={styles.priceDropChipText}>{formatPrice(priceDropAmount)}</Text>
            </View>
            {previousPrice != null && (
              <Text style={styles.previousPriceText}>{formatPrice(previousPrice)}</Text>
            )}
          </View>
        )}
        <Text style={styles.priceText}>{formatPrice(displayPrice)}</Text>
      </View>
    </View>
  );

  // Negotiate CTA section - only shown when onNegotiate prop is provided
  const negotiateCta = (() => {
    // Never show if no handler, sold, or blocklisted
    if (!onNegotiate || isSold || isBlocklisted) return null;

    // If there is already a negotiation on this listing, show the status pill
    if (negotiationStatus) {
      const statusConfig = STATUS_LABELS[negotiationStatus] || {
        label: negotiationStatus,
        color: REDESIGN_COLORS.textSecondary,
      };
      return (
        <View style={styles.negotiateRow}>
          <View style={[styles.negotiateStatusPill, { backgroundColor: `${statusConfig.color}20` }]}>
            <View style={[styles.negotiateStatusDot, { backgroundColor: statusConfig.color }]} />
            <Text style={[styles.negotiateStatusText, { color: statusConfig.color }]}>
              {statusConfig.label}
            </Text>
          </View>
        </View>
      );
    }

    // Show negotiate button (enabled or disabled)
    return (
      <View style={styles.negotiateRow}>
        {showNegLimitInfo && (
          <Pressable
            style={styles.negLimitTooltip}
            onPress={(e) => {
              e?.stopPropagation?.();
              setShowNegLimitInfo(false);
            }}
            accessibilityRole="alert"
          >
            <Text style={styles.negLimitTooltipText}>
              {getNegotiationCapCopy(negotiateLimit)}
            </Text>
            <View style={styles.negLimitTooltipArrow} />
          </Pressable>
        )}
        <Pressable
          style={[
            styles.negotiateButton,
            isNegotiateDisabled && styles.negotiateButtonDisabled,
          ]}
          onPress={handleNegotiate}
          accessibilityRole="button"
          accessibilityLabel="Negotiate price"
          accessibilityState={{ disabled: isNegotiateDisabled }}
          accessibilityHint={isNegotiateDisabled ? `You are at the ${negotiateLimit}-negotiation limit` : undefined}
        >
          <MaterialCommunityIcons
            name="handshake-outline"
            size={14}
            color={isNegotiateDisabled ? REDESIGN_COLORS.textSecondary : '#FFFFFF'}
          />
          <Text
            style={[
              styles.negotiateButtonText,
              isNegotiateDisabled && styles.negotiateButtonTextDisabled,
            ]}
          >
            Negotiate
          </Text>
        </Pressable>
      </View>
    );
  })();

  // The unlock affordance is rendered inside the image component (see
  // imageComponent definition above) so it centers on the image div,
  // not the entire card. Details below keep the blurred preview content
  // so the card retains its normal height.
  const lockedMiddle = (
    <View style={styles.lockedBlur} pointerEvents="none">
      {specsRow}
      {snippetsBlock}
    </View>
  );

  const detailsContent = isLocked ? (
    <View style={[styles.details, !isMobile && styles.detailsDesktop]}>
      <Text style={styles.title} numberOfLines={isMobile ? 2 : 1}>{title}</Text>
      {lockedMiddle}
      <View style={[styles.bottomRow, !isMobile && styles.bottomRowDesktop]}>
        <Text style={[styles.priceText, styles.priceLocked]} selectable={false}>
          {formatPrice(price)}
        </Text>
      </View>
    </View>
  ) : (
    <View style={[styles.details, !isMobile && styles.detailsDesktop, isSold && styles.detailsSold]}>
      <Text style={styles.title} numberOfLines={isMobile ? 2 : 1}>{title}</Text>
      {specsRow}
      {snippetsBlock}
      {bottomRow}
    </View>
  );

  // --- Layout ---

  // RN-web renders accessibilityRole="button" as a <button>, and a <button>
  // can't contain a <button>. So the card is a button ONLY when it has no
  // nested button-role children: the locked overlay (desktop), the blurred
  // match-score badge, and the negotiate CTA. Otherwise it's a plain container
  // (onPress still fires on web) to avoid the nested-<button> hydration error.
  const hasNestedButton =
    isLocked ||
    shouldBlurMatchBadge ||
    (!!onNegotiate && !isSold && !isBlocklisted && !negotiationStatus);
  const cardRole = hasNestedButton ? undefined : 'button';

  if (isMobile) {
    return (
      <Pressable
        style={[styles.cardMobile, styles.cardRelative, isTopMatch && !isLocked && styles.cardTopMatch]}
        onPress={isLocked ? handleUnlockTap : handlePress}
        disabled={isSold && !isLocked}
        accessibilityRole={cardRole}
        accessibilityLabel={isLocked ? 'Locked listing, unlock with Pro' : `${title}, score ${Math.round(score)} percent${isTopMatch ? ', top match' : ''}`}
      >
        {imageComponent}
        {detailsContent}
      </Pressable>
    );
  }

  // Desktop: horizontal layout
  return (
    <Pressable
      style={[styles.cardDesktop, styles.cardRelative, isTopMatch && !isLocked && styles.cardTopMatch]}
      onPress={isLocked ? handleUnlockTap : handlePress}
      disabled={isSold && !isLocked}
      accessibilityRole={cardRole}
      accessibilityLabel={isLocked ? 'Locked listing, unlock with Pro' : `${title}, score ${Math.round(score)} percent${isTopMatch ? ', top match' : ''}`}
    >
      {imageComponent}
      {detailsContent}
      {cardUnlockOverlay}
    </Pressable>
  );
});
FeedListingCard.displayName = 'FeedListingCard';

const styles = StyleSheet.create({
  // Mobile card
  cardMobile: {
    width: '100%',
  },

  // Desktop card
  cardDesktop: {
    width: DESKTOP_CARD_WIDTH,
    flexDirection: 'row',
    gap: 24,
    alignSelf: 'center',
  },

  // Top match — no card-level styling, badge only
  cardTopMatch: {},

  // Image containers
  imageMobile: {
    width: '100%',
    aspectRatio: 326 / 187,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: REDESIGN_COLORS.cardBg,
  },
  imageDesktop: {
    width: DESKTOP_IMAGE_WIDTH,
    height: 260,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: REDESIGN_COLORS.cardBg,
    flexShrink: 0,
  },
  imageGrayscale: {
    filter: 'grayscale(1)',
  },
  imageLocked: {
    filter: 'grayscale(1) blur(10px)',
  },
  placeholderBg: {
    backgroundColor: REDESIGN_COLORS.cardBg,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Action buttons (top-right on image, side by side)
  actionButtonsRow: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    gap: 6,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: REDESIGN_COLORS.buttonOverlayBg,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Details area
  details: {
    flex: 1,
    paddingTop: 12,
  },
  detailsDesktop: {
    paddingTop: 0,
  },
  detailsSold: {
    opacity: 0.5,
  },

  title: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 20,
    color: REDESIGN_COLORS.textPrimary,
  },

  // Specs row
  specsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 8,
    rowGap: 4,
    marginTop: 4,
  },
  specsRowDesktop: {
    marginTop: 8,
    justifyContent: 'flex-start',
    columnGap: 32,
  },
  specItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
  },
  specText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
  },
  specTextDistance: {
    color: REDESIGN_COLORS.textPrimary,
  },

  // Money pill: the below-market £ delta, elevated out of the snippet line.
  moneyPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: 40,
  },
  moneyPillMobile: {
    marginTop: 24,
  },
  moneyPillText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: '#3AE869',
  },

  // Match snippet (replaces checkmarks)
  snippetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 40,
  },
  snippetRowMobile: {
    marginTop: 24,
  },
  // When the money pill already carries the top push, the following snippet
  // sits tight beneath it.
  snippetRowTight: {
    marginTop: 12,
  },
  snippetsBlockMobile: {
    marginBottom: 24,
  },
  cautionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
  },
  snippetText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    lineHeight: 13 * 1.4,
    color: REDESIGN_COLORS.textPrimary,
    flex: 1,
  },
  cautionText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    lineHeight: 13 * 1.4,
    color: 'rgba(255, 159, 67, 0.8)',
    flex: 1,
  },

  // Bottom row (badge + price)
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  bottomRowDesktop: {
    marginTop: 'auto',
  },
  priceText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: REDESIGN_COLORS.textPrimary,
  },
  priceLocked: {
    filter: 'blur(6px)',
    userSelect: 'none',
  },
  priceBlock: {
    alignItems: 'flex-end',
  },
  priceDropRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  priceDropChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: 'rgba(22,163,74,0.10)',
  },
  priceDropChipText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 11,
    color: '#16A34A',
  },
  previousPriceText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
    color: REDESIGN_COLORS.textSecondary,
    textDecorationLine: 'line-through',
  },

  // Desktop carousel controls
  carouselArrowLeft: {
    position: 'absolute',
    left: 12,
    top: '50%',
    marginTop: -16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  carouselArrowRight: {
    position: 'absolute',
    right: 12,
    top: '50%',
    marginTop: -16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  carouselDots: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  carouselDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  carouselDotActive: {
    backgroundColor: '#FFFFFF',
  },

  // Negotiate CTA
  negotiateRow: {
    marginTop: 10,
    position: 'relative',
  },
  negLimitTooltip: {
    position: 'absolute',
    bottom: 42,
    left: 0,
    width: 260,
    maxWidth: '100%',
    backgroundColor: '#0F1B33',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.35,
    shadowRadius: 16,
    zIndex: 10,
  },
  negLimitTooltipText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12.5,
    lineHeight: 17,
    color: '#FFFFFF',
  },
  negLimitTooltipArrow: {
    position: 'absolute',
    bottom: -6,
    left: 24,
    width: 12,
    height: 12,
    backgroundColor: '#0F1B33',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    transform: [{ rotate: '45deg' }],
  },
  negotiateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
  },
  negotiateButtonDisabled: {
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  negotiateButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },
  negotiateButtonTextDisabled: {
    color: REDESIGN_COLORS.textSecondary,
  },
  negotiateStatusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    alignSelf: 'flex-start',
  },
  negotiateStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  negotiateStatusText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11,
  },

  compareCheckbox: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#FFFFFF',
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compareCheckboxSelected: {
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderColor: REDESIGN_COLORS.ctaBlue,
  },

  lockedBlur: {
    marginTop: 4,
    opacity: 0.15,
    filter: 'blur(3px)',
  },
  cardRelative: {
    position: 'relative',
  },
  cardUnlockOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  unlockButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  unlockButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: '#FFFFFF',
  },
});

export default FeedListingCard;
